import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';
import { NEXT_PUBLIC_HOTJAR_DISABLED, NEXT_PUBLIC_HOTJAR_SITE_ID } from '@/lib/env';
import logger from './logger';

// Version of the Hotjar tracking snippet below; coupled to the bootstrap code, not per-environment.
const HOTJAR_SNIPPET_VERSION = 6;

// Hotjar site id, coerced to a positive integer. NaN (unset / unresolved op:// ref / non-numeric) leaves Hotjar unconfigured, so a misconfigured id fails closed rather than rendering invalid JS.
const HOTJAR_SITE_ID = Number(NEXT_PUBLIC_HOTJAR_SITE_ID);
const hasValidSiteId = Number.isInteger(HOTJAR_SITE_ID) && HOTJAR_SITE_ID > 0;

// Hotjar cookies and storage keys are namespaced with `hj`, with an optional leading underscore (e.g. `_hjSessionUser_*`, `hjViewportId`, `hjActiveViewportIds`).
const HOTJAR_KEY_PATTERN = /^_?hj/;

/** Whether Hotjar is configured for this site: a valid site id is set (env) and Hotjar is not disabled. */
export const isHotjarConfigured = (): boolean => hasValidSiteId && !NEXT_PUBLIC_HOTJAR_DISABLED;

/** The inline Hotjar bootstrap snippet, or null when not configured. */
export const getHotjarBootstrapScript = (): string | null => {
  if (!isHotjarConfigured()) {
    return null;
  }

  return `
    (function(h,o,t,j,a,r){
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:${HOTJAR_SITE_ID},hjsv:${HOTJAR_SNIPPET_VERSION}};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
  `;
};

const isHotjarKey = (name: string): boolean => HOTJAR_KEY_PATTERN.test(name);

const clearHotjarStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const cookiesRemoved = clearBrowserCookies(isHotjarKey);
  const localRemoved = clearWebStorage(() => window.localStorage, isHotjarKey);
  const sessionRemoved = clearWebStorage(() => window.sessionStorage, isHotjarKey);

  logger.debug('Cleared Hotjar storage', { cookiesRemoved, localRemoved, sessionRemoved });
};

/**
 * Tear down Hotjar: clear its cookies and storage. An already-loaded snippet keeps running until the next reload, so this stops collection by clearing storage rather than unloading it.
 */
export const disableHotjar = (): void => {
  logger.debug('Hotjar disabled');
  clearHotjarStorage();
};
